// =============================================================================
// VISITES — l'écran du Visit Engine (CTO 2026-09-08)
// =============================================================================
// 🔑 CET ÉCRAN NE CONNAÎT AUCUN MÉTIER. Il rend ce que le PROFIL du tenant
// déclare : sections, questions typées, médias attendus, modèle de rapport.
// ⛔ Aucune zone, aucun libellé, aucun questionnaire n'est écrit ici — c'est
// exactement ce que la constante `ZONES_CONFIG` faisait, et qu'on vient de
// retirer du code.
//
// ⚠️ CE QUI EST « EN CONSTRUCTION » LE DIT, À L'ÉCRAN. Un parcours dont on ne
// sait pas ce qui marche est pire qu'un parcours incomplet : l'utilisateur doit
// voir CE QUI EST LÀ et CE QUI VIENT, sans avoir à essayer pour le découvrir.
// =============================================================================
import React, { useEffect, useState } from "react";
import { ClipboardList, Camera, Mic, FileText, MapPin, AlertTriangle, Check } from "lucide-react";
import { Button } from "../../components/ui/button";
import SaisieVisite from "./SaisieVisite";

// 🔴 MARQUEUR DE BUNDLE — ASCII PUR, VOLONTAIREMENT.
// ⚠️ Les chaînes accentuées sont échappées en \xNN à la minification : un
// marqueur avec accent est INTROUVABLE dans le bundle servi, et le contrôle
// conclurait à tort. Mesuré le 2026-09-03 sur un autre lot.
export const MARQUEUR_VISIT_ENGINE = "OH-VISIT-ENGINE-V1";


const ETAT = {
  actif: { libelle: "Disponible", couleur: "green" },
  construction: { libelle: "En construction", couleur: "amber" },
};

function Pastille({ etat }) {
  const e = ETAT[etat] || ETAT.construction;
  return (
    <span
      style={{
        background: `var(--oh-stat-${e.couleur}-bg)`,
        color: `var(--oh-stat-${e.couleur}-text)`,
        borderRadius: 8,
      }}
      className="px-2 py-0.5 text-xs whitespace-nowrap"
    >
      {e.libelle}
    </span>
  );
}

// Le parcours métier, rendu LISIBLE. ⚠️ L'état vient de ce qui est RÉELLEMENT
// branché aujourd'hui — ⛔ pas d'une intention.
const PARCOURS = [
  { id: "client", libelle: "Client", etat: "actif" },
  { id: "dossier", libelle: "Dossier", etat: "actif" },
  { id: "devis", libelle: "Devis", etat: "actif" },
  // 🔴 Passé à « disponible » le 09/09 : la saisie est PERSISTÉE côté serveur
  // (réponses, quantités, observations) et se reprend après coupure.
  { id: "visite", libelle: "Visite", etat: "actif" },
  { id: "rapport", libelle: "Rapport de visite", etat: "construction" },
];

export default function VisitesModule({ onOuvrirDevis }) {
  const [profils, setProfils] = useState([]);
  const [motif, setMotif] = useState(null);
  const [detail, setDetail] = useState(null);
  const [saisie, setSaisie] = useState(null);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api/visite/profils", { credentials: "include" });
        const j = await r.json();
        if (!r.ok) {
          // ⛔ On AFFICHE le motif du serveur : il nomme ce qu'il faut créer.
          setMotif(j?.detail || `Erreur ${r.status}`);
        } else {
          setProfils(j.profils || []);
          setMotif(j.motif_liste_vide || null);
        }
      } catch (e) {
        setMotif(`Service indisponible : ${e.message}`);
      } finally {
        setChargement(false);
      }
    })();
  }, []);

  const ouvrir = async (code) => {
    setDetail({ code, chargement: true });
    try {
      const r = await fetch(`/api/visite/profils/${code}`, { credentials: "include" });
      const j = await r.json();
      setDetail(r.ok ? { code, profil: j } : { code, erreur: j?.detail });
    } catch (e) {
      setDetail({ code, erreur: e.message });
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="visites-module">
      <div>
        <h1 style={{ color: "var(--oh-text)", fontWeight: 500 }} className="text-xl">
          Visites terrain
        </h1>
        <p style={{ color: "var(--oh-text-secondary)" }} className="text-sm mt-1">
          La visite est guidée par un profil. Le questionnaire est de la
          configuration — il se modifie sans toucher au code.
        </p>
        <span className="text-[10px] opacity-40">{MARQUEUR_VISIT_ENGINE}</span>
      </div>

      {/* ── LE PARCOURS, ET CE QUI EST PRÊT ────────────────────────────── */}
      <div
        style={{
          background: "var(--oh-bg-elevated)",
          border: "0.5px solid var(--oh-border)",
          borderRadius: 12,
          padding: 16,
        }}
      >
        <div style={{ color: "var(--oh-text)", fontWeight: 500 }} className="text-sm mb-3">
          Le parcours
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {PARCOURS.map((e, i) => (
            <React.Fragment key={e.id}>
              <div className="flex items-center gap-2">
                <span style={{ color: "var(--oh-text)" }} className="text-sm">{e.libelle}</span>
                <Pastille etat={e.etat} />
              </div>
              {i < PARCOURS.length - 1 && (
                <span style={{ color: "var(--oh-text-muted)" }}>→</span>
              )}
            </React.Fragment>
          ))}
        </div>
        <p style={{ color: "var(--oh-text-secondary)" }} className="text-xs mt-3">
          « En construction » = l'étape est visible et sa logique est en place,
          mais la saisie n'est pas encore enregistrée.
        </p>
      </div>

      {/* ── LES PROFILS DU TENANT ──────────────────────────────────────── */}
      {chargement && (
        <div style={{ color: "var(--oh-text-secondary)" }} className="text-sm">
          Chargement des profils…
        </div>
      )}

      {!chargement && motif && (
        <div
          style={{
            background: "var(--oh-stat-amber-bg)",
            color: "var(--oh-stat-amber-text)",
            borderRadius: 8,
            padding: 16,
          }}
          data-testid="visites-motif"
        >
          <div className="flex gap-2 items-start">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div className="text-sm whitespace-pre-line">{motif}</div>
          </div>
        </div>
      )}

      {profils.map((p) => (
        <div
          key={p.code}
          style={{
            background: "var(--oh-bg-elevated)",
            border: "0.5px solid var(--oh-border)",
            borderRadius: 12,
            padding: 16,
          }}
          data-testid={`profil-${p.code}`}
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div style={{ color: "var(--oh-text)", fontWeight: 500 }}>{p.libelle}</div>
              <div style={{ color: "var(--oh-text-secondary)" }} className="text-sm mt-1">
                {p.nb_sections} section(s) · {p.nb_questions} question(s) ·
                rapport « {p.modele_rapport} »
              </div>
              {/* 🔴 LE STATUT MÉTIER VIENT DE LA DONNÉE. Un questionnaire que
                  le métier n'a pas relu ⛔ ne doit pas passer pour un
                  référentiel arrêté. */}
              {p.statut_metier === "brouillon_a_valider" && (
                <div
                  style={{
                    background: "var(--oh-stat-amber-bg)",
                    color: "var(--oh-stat-amber-text)",
                    borderRadius: 8,
                  }}
                  className="text-xs mt-2 px-2 py-1 inline-block"
                >
                  Questionnaire provisoire — à valider par le métier
                </div>
              )}
              {p.defaut && (
                <div
                  style={{
                    background: "var(--oh-stat-red-bg)",
                    color: "var(--oh-stat-red-text)",
                    borderRadius: 8,
                  }}
                  className="text-xs mt-2 px-2 py-1"
                >
                  {p.defaut}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => ouvrir(p.code)}
                style={{
                  borderColor: "var(--oh-border)",
                  color: "var(--oh-text-secondary)",
                  background: "transparent",
                }}
              >
                Voir le questionnaire
              </Button>
              {/* 🔴 L'action PRINCIPALE de l'écran — une seule par carte. */}
              <Button
                onClick={() => setSaisie(saisie === p.code ? null : p.code)}
                data-testid={`demarrer-${p.code}`}
                style={{
                  background: "var(--oh-accent-solid)",
                  color: "var(--oh-accent-contrast)",
                }}
              >
                {saisie === p.code ? "Fermer la saisie" : "Démarrer la visite"}
              </Button>
            </div>
          </div>

          {detail?.code === p.code && detail.profil && (
            <div className="mt-4 space-y-3">
              {(detail.profil.sections || []).map((s) => (
                <div
                  key={s.id}
                  style={{
                    background: "var(--oh-bg-surface)",
                    border: "0.5px solid var(--oh-border)",
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    {s.type === "zone" ? <MapPin size={14} /> : <ClipboardList size={14} />}
                    <span style={{ color: "var(--oh-text)", fontWeight: 500 }} className="text-sm">
                      {s.libelle}
                    </span>
                    {s.medias?.photos?.requis && (
                      <span
                        style={{
                          background: "var(--oh-stat-blue-bg)",
                          color: "var(--oh-stat-blue-text)",
                          borderRadius: 8,
                        }}
                        className="text-xs px-2 py-0.5 flex items-center gap-1"
                      >
                        <Camera size={11} /> photo requise
                      </span>
                    )}
                  </div>
                  <ul className="mt-2 space-y-1">
                    {(s.questions || []).map((q) => (
                      <li
                        key={q.id}
                        style={{ color: "var(--oh-text-secondary)" }}
                        className="text-sm flex items-start gap-2"
                      >
                        <Check size={13} className="mt-0.5 shrink-0" />
                        <span>
                          {q.libelle}
                          <span style={{ color: "var(--oh-text-muted)" }} className="text-xs">
                            {" "}· {q.type}
                            {q.unite ? ` (${q.unite})` : ""}
                          </span>
                          {q.problematique && (
                            <span
                              style={{ color: "var(--oh-text-muted)" }}
                              className="block text-xs italic"
                            >
                              {q.problematique}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* ── CE QUI VIENT, DIT EXPLICITEMENT ─────────────────────── */}
              <div
                style={{
                  background: "var(--oh-stat-amber-bg)",
                  color: "var(--oh-stat-amber-text)",
                  borderRadius: 8,
                  padding: 12,
                }}
                data-testid="visites-a-venir"
              >
                <div className="text-sm font-medium mb-1">Ce qui n'est pas encore branché</div>
                <ul className="text-xs space-y-1">
                  <li className="flex items-center gap-2">
                    <Camera size={12} /> Photos par section — capture et stockage
                  </li>
                  <li className="flex items-center gap-2">
                    <Mic size={12} /> Dictée des observations
                  </li>
                  <li className="flex items-center gap-2">
                    <FileText size={12} /> Rapport « {detail.profil.modele_rapport?.code} »
                    généré depuis cette visite
                  </li>
                </ul>
              </div>
            </div>
          )}

          {saisie === p.code && (
            <div className="mt-4">
              <SaisieVisite profilCode={p.code} profilLibelle={p.libelle}
                            onOuvrirDevis={onOuvrirDevis} />
            </div>
          )}

          {detail?.code === p.code && detail.erreur && (
            <div
              style={{
                background: "var(--oh-stat-red-bg)",
                color: "var(--oh-stat-red-text)",
                borderRadius: 8,
              }}
              className="text-sm mt-3 p-3 whitespace-pre-line"
            >
              {detail.erreur}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
